import { type FormEvent, type ReactElement, useState } from 'react';
import { useProductsListQuery } from '@/entities/product';
import { Button, Input } from '@/shared/ui';

interface ProductSearchListProps {
  /** 각 행 버튼 문구 — "선택"·"이 상품에서 복사". */
  readonly actionLabel: string;
  readonly excludeProductId?: string;
  readonly selectedProductId?: string | null;
  readonly disabled?: boolean;
  readonly onPick: (product: { readonly id: string; readonly name: string }) => void;
}

/** 상품명으로 찾아 하나를 고르는 목록 — 템플릿 원본·복사 원본 선택에 쓴다. 상태와 무관하게 전체 상품을 찾는다. */
export const ProductSearchList = ({
  actionLabel,
  excludeProductId,
  selectedProductId = null,
  disabled = false,
  onPick,
}: ProductSearchListProps): ReactElement => {
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const query = useProductsListQuery({ page: 1, status: '', search });
  const products = (query.data?.items ?? []).filter((product) => product.id !== excludeProductId);

  const handleSearch = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setSearch(draft.trim());
  };

  return (
    <div className="space-y-3">
      <form onSubmit={handleSearch} className="flex gap-2">
        <Input
          aria-label="상품명 검색"
          placeholder="상품명"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        <Button type="submit" size="sm" variant="outline">
          검색
        </Button>
      </form>
      {query.isPending ? (
        <p className="text-sm text-muted-foreground">불러오는 중…</p>
      ) : query.isError ? (
        <p role="alert" className="text-sm text-destructive">
          상품을 불러오지 못했습니다.
        </p>
      ) : products.length === 0 ? (
        <p className="text-sm text-muted-foreground">검색 결과가 없습니다.</p>
      ) : (
        <ul className="flex max-h-72 flex-col gap-2 overflow-y-auto">
          {products.map((product) => (
            <li key={product.id} className="flex items-center gap-3">
              {product.thumbnail !== null ? (
                <img
                  src={product.thumbnail.url}
                  alt=""
                  width={32}
                  height={32}
                  className="size-8 rounded object-cover"
                />
              ) : (
                <span aria-hidden className="size-8 rounded bg-muted" />
              )}
              <span className="min-w-0 flex-1 truncate text-sm">{product.name}</span>
              <Button
                type="button"
                size="sm"
                variant={selectedProductId === product.id ? 'default' : 'outline'}
                disabled={disabled}
                aria-label={`${product.name} ${actionLabel}`}
                onClick={() => onPick({ id: product.id, name: product.name })}
              >
                {selectedProductId === product.id ? '선택됨' : actionLabel}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
